import { RequestBody } from '@/api/react-query.config';

type CreateQuestionRequest = RequestBody<'/api/admin/questions', 'post'>;
type ApiOption = NonNullable<CreateQuestionRequest['options']>[number];

export type QuestionFormValues = Omit<CreateQuestionRequest, 'options'> & {
  display_order?: number;
  options: Array<
    ApiOption & {
      option_id?: string;
      is_correct: boolean;
      display_order?: number;
      grading_data?: {
        teamId?: number;
        playerId?: number;
        playerName?: string;
        threshold?: number;
        comparison?: 'over' | 'under';
      };
    }
  >;
  sportcc_match_id?: number | null;
  sportcc_match_data?: {
    homeTeam: { id: number; name: string };
    awayTeam: { id: number; name: string };
    league: string;
    matchDate: string;
    status: string;
  } | null;
  question_metadata?: {
    grading_type?: string;
    config?: {
      threshold?: number;
      comparison?: 'over' | 'under';
      team_id?: number;
      team_name?: string;
      player_count?: number;
      player_id?: number;
      player_name?: string;
    };
  };
};

export interface QuestionData {
  question_id?: string;
  pickem_id?: string;
  question_text_en?: string;
  question_text_fr?: string;
  question_subtext_en?: string;
  question_subtext_fr?: string;
  question_image_en?: string;
  question_image_fr?: string;
  start_utc?: string;
  question_type?: string;
  points?: number;
  correct_value?: number | string;
  display_order?: number;
  sportcc_match_id?: number | null;
  sportcc_match_data?: {
    homeTeam: { id: number; name: string };
    awayTeam: { id: number; name: string };
    league: string;
    matchDate: string;
    status: string;
  } | null;
  question_metadata?: {
    grading?: {
      type:
        | 'match_winner'
        | 'total_goals_over_under'
        | 'team_goals_over_under'
        | 'player_goals_over_under'
        | 'player_assists_over_under'
        | 'player_points_over_under'
        | 'grouped_player_most_points';
      sportccMatchId: number;
      config?: {
        threshold?: number;
        comparison?: 'over' | 'under';
        statType?: 'goals' | 'assists' | 'points';
      };
    };
  } | null;
  options?: Array<{
    option_id?: string;
    question_id?: string;
    option_text_en?: string;
    option_text_fr?: string;
    option_image_en?: string;
    option_image_fr?: string;
    third_image_en?: string;
    third_image_fr?: string;
    start_utc?: string;
    is_correct?: boolean | string;
    display_order?: number;
    grading_data?: {
      teamId?: number;
      playerId?: number;
      playerName?: string;
      threshold?: number;
      comparison?: 'over' | 'under';
    } | null;
    created_at?: string;
  }>;
}

export function apiToFormValues(
  question: QuestionData | undefined,
  searchPickemId?: string
): QuestionFormValues {
  const questionType = (question?.question_type as any) || 'team';
  const isTiebreaker = questionType === 'tiebreaker';

  return {
    pickem_id: question?.pickem_id || searchPickemId,
    question_text_en: question?.question_text_en || '',
    question_text_fr: question?.question_text_fr,
    question_subtext_en: question?.question_subtext_en,
    question_subtext_fr: question?.question_subtext_fr,
    question_image_en: question?.question_image_en,
    question_image_fr: question?.question_image_fr,
    start_utc: question?.start_utc
      ? new Date(question.start_utc).toISOString().slice(0, 16)
      : undefined,
    question_type: questionType,
    points: isTiebreaker ? 0 : question?.points || 5,
    correct_value:
      question?.correct_value !== undefined
        ? typeof question.correct_value === 'string'
          ? parseFloat(question.correct_value)
          : question.correct_value
        : undefined,
    display_order: question?.display_order,
    sportcc_match_id: question?.sportcc_match_id ?? null,
    sportcc_match_data: question?.sportcc_match_data ?? null,
    question_metadata: (question?.question_metadata as any) ?? undefined,
    options: isTiebreaker
      ? []
      : question?.options && question.options.length > 0
        ? question.options.map((opt, index) => ({
            option_id: opt.option_id,
            option_text_en: opt.option_text_en || '',
            option_text_fr: opt.option_text_fr,
            option_image_en: opt.option_image_en,
            option_image_fr: opt.option_image_fr,
            third_image_en: opt.third_image_en,
            third_image_fr: opt.third_image_fr,
            start_utc: opt.start_utc,
            is_correct: opt.is_correct === true,
            display_order: opt.display_order ?? index,
            grading_data: opt.grading_data ?? undefined,
          }))
        : [
            {
              option_text_en: '',
              option_text_fr: undefined,
              option_image_en: undefined,
              option_image_fr: undefined,
              third_image_en: undefined,
              third_image_fr: undefined,
              start_utc: undefined,
              is_correct: false,
              display_order: 0,
            },
            {
              option_text_en: '',
              option_text_fr: undefined,
              option_image_en: undefined,
              option_image_fr: undefined,
              third_image_en: undefined,
              third_image_fr: undefined,
              start_utc: undefined,
              is_correct: false,
              display_order: 1,
            },
          ],
  };
}

export function formValuesToCreatePayload(values: QuestionFormValues): CreateQuestionRequest {
  const isTiebreaker = values.question_type === 'tiebreaker';
  console.log('🟢 formValuesToCreatePayload');
  console.log('Is Tiebreaker:', isTiebreaker);
  console.log('Input Options:', values.options);

  const formattedOptions = isTiebreaker
    ? undefined
    : values.options.map((opt, index) => ({
        option_id: opt.option_id,
        option_text_en: opt.option_text_en,
        option_text_fr: opt.option_text_fr,
        option_image_en: opt.option_image_en,
        option_image_fr: opt.option_image_fr,
        third_image_en: opt.third_image_en,
        third_image_fr: opt.third_image_fr,
        start_utc: opt.start_utc,
        is_correct: opt.is_correct,
        display_order: index,
        grading_data: opt.grading_data,
      }));

  console.log('Formatted Options:', formattedOptions);

  const payload: CreateQuestionRequest = {
    pickem_id: values.pickem_id,
    question_text_en: values.question_text_en,
    question_text_fr: values.question_text_fr,
    question_subtext_en: values.question_subtext_en,
    question_subtext_fr: values.question_subtext_fr,
    question_image_en: values.question_image_en,
    question_image_fr: values.question_image_fr,
    start_utc: values.start_utc,
    question_type: values.question_type,
    points: values.points,
    correct_value: values.correct_value,
    options: formattedOptions,
  };

  // Only include SportCC fields if match is selected
  if (values.sportcc_match_id) {
    payload.sportcc_match_id = values.sportcc_match_id;
    payload.sportcc_match_data = values.sportcc_match_data;
  }

  // Include question metadata if present
  if (values.question_metadata) {
    payload.question_metadata = values.question_metadata;
  }

  return payload;
}

export function formValuesToUpdatePayload(questionId: string, values: QuestionFormValues) {
  const questionType = values.question_type || 'team';
  const isTiebreaker = questionType === 'tiebreaker';
  const categoryMap: Record<string, string> = {
    team: 'TEAM_SELECTION',
    player: 'PLAYER_SELECTION',
    prop: 'YES_NO',
    tiebreaker: 'PCT_OPTIONS',
  };
  const viewTypeMap: Record<string, string> = {
    team: 'TEAM_SELECTION',
    player: 'PLAYER_SELECTION',
    prop: 'YES_NO',
    tiebreaker: 'PCT_OPTIONS',
  };

  if (!values.pickem_id) {
    throw new Error('pickem_id is required for update');
  }

  const formattedOptions = isTiebreaker
    ? []
    : values.options.map((opt, index) => ({
        option_id: opt.option_id,
        option_text_en: opt.option_text_en,
        option_text_fr: opt.option_text_fr,
        option_image_en: opt.option_image_en,
        option_image_fr: opt.option_image_fr,
        third_image_en: opt.third_image_en,
        third_image_fr: opt.third_image_fr,
        start_utc: opt.start_utc,
        is_correct: opt.is_correct,
        display_order: index,
        grading_data: opt.grading_data,
      }));

  const payload: any = {
    pickemId: values.pickem_id,
    data: {
      question_id: questionId,
      title_en: values.question_text_en,
      title_fr: values.question_text_fr,
      description_en: values.question_subtext_en || '',
      description_fr: values.question_subtext_fr,
      main_image: values.question_image_en,
      secondary_image: values.question_image_fr,
      start_utc: values.start_utc || undefined,
      question_type: values.question_type,
      category: categoryMap[questionType] || 'TEAM_SELECTION',
      view_type: viewTypeMap[questionType] || 'TEAM_SELECTION',
      points: values.points,
      correct_value: values.correct_value,
      order: values.display_order ?? 0,
      options: formattedOptions.map(opt => ({
        option_id: opt.option_id,
        title_en: opt.option_text_en,
        title_fr: opt.option_text_fr,
        third_image_en: opt.third_image_en,
        third_image_fr: opt.third_image_fr,
        option_image: opt.option_image_en,
        option_secondary_image: opt.option_image_fr,
        option_third_image: opt.third_image_en,
        label: opt.option_text_en,
        value: opt.option_text_en,
        is_correct: opt.is_correct,
        display_order: opt.display_order,
      })),
    },
  };

  // Only include SportCC fields if match is selected
  if (values.sportcc_match_id) {
    payload.data.sportcc_match_id = values.sportcc_match_id;
    payload.data.sportcc_match_data = values.sportcc_match_data;
  }

  // Include question metadata if present
  if (values.question_metadata) {
    payload.data.question_metadata = values.question_metadata;
  }

  return payload;
}
